import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Bold, Italic, AlignLeft, AlignCenter, AlignRight, Type, Palette, PaintBucket, Square } from 'lucide-react';
import { TextStyle, StageTransform } from './types';

export interface TextFormatToolbarProps {
  textStyle: TextStyle;
  onStyleChange: (updates: Partial<TextStyle>) => void;
  onToggleBold: () => void;
  onToggleItalic: () => void;
  position?: { x: number; y: number };
  stageTransform?: { scale: number; x: number; y: number };
  /** Bounds of the element being edited in screen coordinates */
  elementBounds?: { x: number; y: number; width: number; height: number };
  /** Container element for viewport bounds calculation */
  containerRef?: React.RefObject<HTMLDivElement>;
  backgroundColor?: string;
  onBackgroundColorChange?: (color: string) => void;
  strokeColor?: string;
  onStrokeColorChange?: (color: string) => void;
}

const FONT_FAMILIES = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Courier New',
  'Verdana',
  'Comic Sans MS',
  'Impact',
];

const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64];

/**
 * TextFormatToolbar provides formatting controls for text editing
 * Includes: bold, italic, font size, font family, alignment, and color picker
 */
export const TextFormatToolbar: React.FC<TextFormatToolbarProps> = ({
  textStyle,
  onStyleChange,
  onToggleBold,
  onToggleItalic,
  position,
  stageTransform,
  elementBounds,
  containerRef,
  backgroundColor,
  onBackgroundColorChange,
  strokeColor,
  onStrokeColorChange,
}) => {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarSize, setToolbarSize] = useState({ width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [isPositionedAbove, setIsPositionedAbove] = useState(false);
  
  const isBold = textStyle.fontStyle.includes('bold');
  const isItalic = textStyle.fontStyle.includes('italic');

  const scale = stageTransform?.scale || 1;
  const stageX = stageTransform?.x || 0;
  const stageY = stageTransform?.y || 0;
  
  // Measure toolbar size
  useEffect(() => {
    if (toolbarRef.current) {
      const rect = toolbarRef.current.getBoundingClientRect();
      setToolbarSize({ width: rect.width, height: rect.height });
    }
  }, []);

  // Trigger animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Calculate viewport-aware position
  const calculatePosition = () => {
    const TOOLBAR_HEIGHT = 50; // Approximate toolbar height
    const TOOLBAR_MARGIN = 20; // Margin from element
    const VIEWPORT_PADDING = 10; // Padding from viewport edges
    
    // Get container bounds for viewport calculation
    const containerBounds = containerRef?.current?.getBoundingClientRect() || {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      width: window.innerWidth,
      height: window.innerHeight,
    };
    
    let x: number | undefined;
    let y: number | undefined;
    let positionedAbove = false;
    
    if (elementBounds) {
      // Calculate available space above and below the element
      const spaceAbove = elementBounds.y - containerBounds.top;
      const spaceBelow = containerBounds.bottom - (elementBounds.y + elementBounds.height);
      
      // Position horizontally centered on element
      x = elementBounds.x + elementBounds.width / 2;
      
      // Position above or below based on available space
      if (spaceAbove >= TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Position above element
        y = elementBounds.y - TOOLBAR_MARGIN;
        positionedAbove = true;
      } else if (spaceBelow >= TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Position below element
        y = elementBounds.y + elementBounds.height + TOOLBAR_HEIGHT + TOOLBAR_MARGIN;
        positionedAbove = false;
      } else {
        // Not enough space above or below, position at top of viewport
        y = containerBounds.top + VIEWPORT_PADDING + TOOLBAR_HEIGHT;
        positionedAbove = true;
      }
      
      // Clamp horizontal position to stay within viewport
      const toolbarWidth = toolbarSize.width || 400; // Estimated width
      const halfWidth = toolbarWidth / 2;
      x = Math.max(containerBounds.left + halfWidth + VIEWPORT_PADDING, x);
      x = Math.min(containerBounds.right - halfWidth - VIEWPORT_PADDING, x);
    } else if (position) {
      // Fallback to old behavior with stageTransform
      x = position.x * scale + stageX;
      y = position.y * scale + stageY;
      
      // Check if there's enough space above the position
      const spaceAbove = y - containerBounds.top;
      if (spaceAbove < TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Not enough space above, position below
        y = y + TOOLBAR_HEIGHT + TOOLBAR_MARGIN * 2;
        positionedAbove = false;
      } else {
        y = y - TOOLBAR_MARGIN;
        positionedAbove = true;
      }
    }
    
    return { x, y, positionedAbove };
  };
  
  const calculatedPosition = useMemo(() => calculatePosition(), [
    elementBounds?.x,
    elementBounds?.y,
    elementBounds?.width,
    elementBounds?.height,
    position?.x,
    position?.y,
    toolbarSize.width,
    scale,
    stageX,
    stageY,
  ]);

  // Update position state when position changes
  useEffect(() => {
    setIsPositionedAbove(calculatedPosition.positionedAbove);
  }, [calculatedPosition.positionedAbove]);

  // Determine transform based on position and animation state
  let transformValue = '';
  if (calculatedPosition.x) {
    const baseTransform = 'translate(-50%, -100%)';
    const slideOffset = isPositionedAbove ? '-20px' : '20px';
    const translateY = isVisible ? '0' : slideOffset;
    transformValue = `${baseTransform} translateY(${translateY})`;
  }

  const toolbarStyle: React.CSSProperties = {
    position: position || elementBounds ? 'absolute' : 'relative',
    left: calculatedPosition.x ? `${calculatedPosition.x}px` : undefined,
    top: calculatedPosition.y ? `${calculatedPosition.y}px` : undefined,
    transform: transformValue || undefined,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px',
    backgroundColor: 'white',
    borderRadius: '9999px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid rgb(229, 231, 235)',
    zIndex: 10001, // Above the backdrop (9999) and text editor
    fontSize: '14px',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    opacity: isVisible ? 1 : 0,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const buttonStyle = (isActive: boolean): React.CSSProperties => ({
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: isActive ? 'rgba(0, 134, 201, 0.1)' : 'transparent',
    color: isActive ? '#0086C9' : 'rgb(107, 114, 128)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  });

  // Helper function to handle toolbar interactions without preventing blur
  const handleToolbarAction = (action: () => void) => {
    // Execute the action immediately
    action();
    // Let the text editor handle blur naturally - don't prevent it
  };

  const selectStyle: React.CSSProperties = {
    padding: '4px 8px',
    border: '1px solid rgb(229, 231, 235)',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '13px',
    color: 'rgb(55, 65, 81)',
    outline: 'none',
  };

  const colorInputStyle: React.CSSProperties = {
    width: '40px',
    height: '32px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
  };

  const dividerStyle: React.CSSProperties = {
    width: '1px',
    height: '24px',
    backgroundColor: 'rgb(229, 231, 235)',
    margin: '0 4px',
  };

  return (
    <div ref={toolbarRef} style={toolbarStyle}>
      {/* Bold */}
      <button
        style={buttonStyle(isBold)}
        onClick={(e) => {
          e.stopPropagation();
          handleToolbarAction(() => onToggleBold());
        }}
        title="Bold (Ctrl+B)"
      >
        <Bold size={16} strokeWidth={isBold ? 2.5 : 2} />
      </button>

      {/* Italic */}
      <button
        style={buttonStyle(isItalic)}
        onClick={(e) => {
          e.stopPropagation();
          handleToolbarAction(() => onToggleItalic());
        }}
        title="Italic (Ctrl+I)"
      >
        <Italic size={16} strokeWidth={isItalic ? 2.5 : 2} />
      </button>

      <div style={dividerStyle} />

      {/* Font Size */}
      <select
        value={textStyle.fontSize}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onChange={(e) => {
          handleToolbarAction(() => onStyleChange({ fontSize: Number(e.target.value) }));
          e.target.blur();
        }}
        onBlur={() => {
          // Allow natural blur behavior
        }}
        style={selectStyle}
        title="Font Size"
      >
        {FONT_SIZES.map((size) => (
          <option key={size} value={size}>
            {size}px
          </option>
        ))}
      </select>

      {/* Font Family */}
      <select
        value={textStyle.fontFamily}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        onChange={(e) => {
          handleToolbarAction(() => onStyleChange({ fontFamily: e.target.value }));
          e.target.blur();
        }}
        onBlur={() => {
          // Allow natural blur behavior
        }}
        style={selectStyle}
        title="Font Family"
      >
        {FONT_FAMILIES.map((font) => (
          <option key={font} value={font}>
            {font}
          </option>
        ))}
      </select>

      <div style={dividerStyle} />

      {/* Text Alignment */}
      <button
        style={buttonStyle(textStyle.align === 'left')}
        onClick={(e) => {
          e.stopPropagation();
          handleToolbarAction(() => onStyleChange({ align: 'left' }));
        }}
        title="Align Left"
      >
        <AlignLeft size={16} strokeWidth={textStyle.align === 'left' ? 2.5 : 2} />
      </button>

      <button
        style={buttonStyle(textStyle.align === 'center')}
        onClick={(e) => {
          e.stopPropagation();
          handleToolbarAction(() => onStyleChange({ align: 'center' }));
        }}
        title="Align Center"
      >
        <AlignCenter size={16} strokeWidth={textStyle.align === 'center' ? 2.5 : 2} />
      </button>

      <button
        style={buttonStyle(textStyle.align === 'right')}
        onClick={(e) => {
          e.stopPropagation();
          handleToolbarAction(() => onStyleChange({ align: 'right' }));
        }}
        title="Align Right"
      >
        <AlignRight size={16} strokeWidth={textStyle.align === 'right' ? 2.5 : 2} />
      </button>

      <div style={dividerStyle} />

      {/* Text Color */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <button
          style={{
            ...buttonStyle(false),
            position: 'relative',
          }}
          onMouseDown={(e) => e.preventDefault()}
          title="Text Color"
        >
          <Palette size={16} strokeWidth={2} />
          <div
            style={{
              position: 'absolute',
              bottom: '2px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '16px',
              height: '3px',
              backgroundColor: textStyle.fill,
              borderRadius: '2px',
            }}
          />
        </button>
        <input
          type="color"
          value={textStyle.fill}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onChange={(e) => {
            handleToolbarAction(() => onStyleChange({ fill: e.target.value }));
          }}
          style={{
            position: 'absolute',
            left: 0,
            width: '32px',
            height: '32px',
            opacity: 0,
            cursor: 'pointer',
          }}
          title="Text Color"
        />
      </div>

      {/* Background Color */}
      {backgroundColor !== undefined && onBackgroundColorChange && (
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <button
            style={{
              ...buttonStyle(false),
              position: 'relative',
            }}
            onMouseDown={(e) => e.preventDefault()}
            title="Background Color"
          >
            <PaintBucket size={16} strokeWidth={2} />
            <div
              style={{
                position: 'absolute',
                bottom: '2px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '16px',
                height: '3px',
                backgroundColor: backgroundColor,
                borderRadius: '2px',
              }}
            />
          </button>
          <input
            type="color"
            value={backgroundColor}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              handleToolbarAction(() => onBackgroundColorChange(e.target.value));
            }}
            style={{
              position: 'absolute',
              left: 0,
              width: '32px',
              height: '32px',
              opacity: 0,
              cursor: 'pointer',
            }}
            title="Background Color"
          />
        </div>
      )}

      {/* Stroke/Border Color */}
      {strokeColor !== undefined && onStrokeColorChange && (
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <button
            style={{
              ...buttonStyle(false),
              position: 'relative',
            }}
            onMouseDown={(e) => e.preventDefault()}
            title="Border Color"
          >
            <Square size={16} strokeWidth={2} />
            <div
              style={{
                position: 'absolute',
                bottom: '2px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '16px',
                height: '3px',
                backgroundColor: strokeColor,
                borderRadius: '2px',
              }}
            />
          </button>
          <input
            type="color"
            value={strokeColor}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              handleToolbarAction(() => onStrokeColorChange(e.target.value));
            }}
            style={{
              position: 'absolute',
              left: 0,
              width: '32px',
              height: '32px',
              opacity: 0,
              cursor: 'pointer',
            }}
            title="Border Color"
          />
        </div>
      )}
    </div>
  );
};

/**
 * FloatingTextFormatToolbar wraps the toolbar in a positioned container
 * Can be positioned near the text being edited
 */
export const FloatingTextFormatToolbar: React.FC<TextFormatToolbarProps> = (props) => {
  // If no position or elementBounds, render without positioning
  if (!props.position && !props.elementBounds) {
    return <div data-text-toolbar><TextFormatToolbar {...props} /></div>;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10001, // Above the backdrop
      }}
    >
      <div style={{ pointerEvents: 'all' }} data-text-toolbar>
        <TextFormatToolbar {...props} />
      </div>
    </div>
  );
};
